import {
  ApiCombination,
  ApiCombinationExpression,
  ApiComparison,
  ApiComparisonExpression,
  ApiExpression,
  ApiField,
  ApiNestedFunction,
  ApiNestedFunctionExpression,
  ApiSimpleFunction,
  ApiSimpleFunctionExpression,
} from './odata';

export type Filters<T> = { [K in keyof T]: ApiField | ApiField[] | FulltextSearch | Filters<any> };

function createSearchExpression(fields: string[], values: string[], valueType: ValueType): ApiExpression {
  const clonedField = structuredClone(fields);
  const field = clonedField.splice(0, 1)[0];
  if (clonedField.length > 0) {
    const alias = generateAlias(10);
    clonedField[0] = `${alias}/${clonedField[0]}`;
    return [field, ApiNestedFunction.Any, alias, createSearchExpression(clonedField, values, valueType)];
  }

  switch (valueType) {
    case ValueType.Naked: {
      const expressions: ApiComparisonExpression[] = [];
      for (const value of values) {
        const numValue = parseFloat(value);
        if (Number.isNaN(numValue)) continue;

        expressions.push([ApiComparison.Equals, {field}, numValue]);
      }
      return [ApiCombination.And, ...expressions];
    }
    case ValueType.StringContains:
      return [ApiCombination.And, ...values.map((v) => [
        ApiSimpleFunction.Contains,
        [ApiSimpleFunction.ToLower, {field}],
        v.toLowerCase(),
      ] as ApiSimpleFunctionExpression)];
    case ValueType.NumberStartsWith: {
      const expressions: ApiSimpleFunctionExpression[] = [];
      for (const value of values) {
        expressions.push([ApiSimpleFunction.StartsWith, [ApiSimpleFunction.Cast, {field}, 'Edm.String'], value]);
      }
      return [ApiCombination.And, ...expressions];
    }
    case ValueType.StringEquals:
      return [
        ApiCombination.And,
        ...values.map((v) => [
          ApiComparison.Equals,
          [ApiSimpleFunction.ToLower, {field}] as ApiSimpleFunctionExpression,
          v.toLowerCase()] as ApiComparisonExpression),
      ];
  }
}

export function createExpressionFromFulltextSearch(fulltext: FulltextSearch): ApiExpression {
  const expr = fulltext.fields.reduce((cum, field) => {
    cum.push(createSearchExpression(field.path, fulltext.words, field.valueType));
    return cum;
  }, [] as ApiExpression[]);
  console.log(expr);

  return [ApiCombination.Or, ...expr];
}


export enum FilterType {
  From = 'From',
  To = 'To',
  Exist = 'Exist',
  In = 'In',
  Any = 'Any',
  ContainsOne = 'ContainsOne',
  Contains = 'Contains',
  Fulltext = 'Fulltext',
  None = '',
}

function isFilters<T>(filter: Filters<T>[keyof Filters<T>]): filter is Filters<any> {
  return typeof filter === 'object' && filter !== null;
}

const firstAliasCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const aliasCharacters = `${firstAliasCharacters}0123456789`;

function generateAlias(length: number): string {
  let result = '';
  result += firstAliasCharacters.charAt(Math.floor(Math.random() * firstAliasCharacters.length));
  for (let i = 1; i < length; i++) {
    result += aliasCharacters.charAt(Math.floor(Math.random() * aliasCharacters.length));
  }
  return result;
}

export enum ValueType {
  StringContains,
  StringEquals,
  Naked,
  NumberStartsWith,
}

export interface FulltextSearchField {
  path: string[];
  valueType: ValueType,
}

export interface FulltextSearch {
  words: string[];
  fields: FulltextSearchField[];
}

export function isFulltext(data: unknown): data is FulltextSearch {
  return typeof data === 'object' && data !== null && 'fields' in data && 'words' in data && Array.isArray(data.fields) && Array.isArray(data.words);
}

type primitive = number | string | null;

function validateNoArray(data: any, key: string): data is any[] {
  if (Array.isArray(data)) throw new Error(`Falscher Typ für ${key}. Bitte $In benutzen für Arrays`);
  return true;
}

function validateArray(data: any, key: string): data is any[] {
  if (!Array.isArray(data)) throw new Error(`Falscher Typ für ${key}. Bitte Array übergeben`);
  return false;
}

function validateFilters<T>(data: Filters<T>[keyof Filters<T>], key: string): data is Filters<any> {
  if (!isFilters(data)) throw new Error(`Falscher Typ für ${key}. Bitte Filter übergeben`);
  return true;
}

function validateNoFulltext(data: any, key: string): data is FulltextSearch {
  if (isFulltext(data)) throw new Error(`Falscher Typ für ${key}. Bitte für Volltextsuchen $Fulltext benutzen`);
  return false;
}

function validateFulltext(data: any, key: string): data is FulltextSearch {
  if (!isFulltext(data)) throw new Error(`Falscher Typ für ${key}. Das ist eine Volltextsuche und benötigt dafür angepasste Daten`);
  return true;
}

export function parseGenericFilters<T>(filters: Filters<T>, combination: ApiCombination = ApiCombination.And, alias: string = '') {
  const convertedFilters: ApiExpression = [combination];
  // eslint-disable-next-line guard-for-in
  for (const key in filters) {
    const current = filters[key];
    if (current === undefined) continue;

    const splitKey = key.split('$');

    const field = alias ? `${alias}/${splitKey[0]}` : splitKey[0];
    const type: FilterType = splitKey[1] as FilterType ?? FilterType.None;

    switch (type) {
      case FilterType.From:
        validateNoArray(current, key);
        convertedFilters.push([ApiComparison.GreaterThanEqual, {field}, current as primitive]);
        break;
      case FilterType.To:
        validateNoArray(current, key);
        convertedFilters.push([ApiComparison.LessThanEqual, {field}, current as primitive]);
        break;
      case FilterType.Exist:
        validateNoArray(current, key);
        if (current === '') continue;
        convertedFilters.push([current ? ApiComparison.GreaterThan : ApiComparison.LessThanEqual, {field: `${field}/$count`}, 0]);
        break;
      case FilterType.Any: {
        validateFilters(current, key);
        validateNoFulltext(current, key);
        const alias = generateAlias(10);
        const expression: ApiNestedFunctionExpression = [
          field,
          ApiNestedFunction.Any,
          alias,
          parseGenericFilters(current as Filters<any>, undefined, alias),
        ];
        convertedFilters.push(expression);
        break;
      }
      case FilterType.ContainsOne:
        validateArray(current, key);
        convertedFilters
            .push([
              ApiCombination.Or,
              ...(current as string[]).map((cur) => [
                ApiSimpleFunction.Contains, [ApiSimpleFunction.ToLower, {field}], cur.toLowerCase(),
              ] as ApiSimpleFunctionExpression),
            ]);
        break;
      case FilterType.Contains:
        validateNoArray(current, key);
        convertedFilters.push([ApiSimpleFunction.Contains, [ApiSimpleFunction.ToLower, {field}], (current as string).toLowerCase()]);
        break;
      case FilterType.Fulltext:
        validateFulltext(current, key);
        convertedFilters.push(createExpressionFromFulltextSearch(current as FulltextSearch));
        break;
      case FilterType.In: {
        validateArray(current, key);
        const expression: ApiCombinationExpression =
          [
            ApiCombination.Or,
            ...((current as ApiField[]).map((i) => [ApiComparison.Equals, {field}, i] as ApiComparisonExpression)),
          ];
        convertedFilters.push(expression);
        break;
      }
      default:
        validateNoArray(current, key);
        convertedFilters.push([ApiComparison.Equals, {field}, current as primitive]);
        break;
    }
  }

  return convertedFilters;
}
